import React, { useMemo, useEffect, useState, useRef } from 'reactn'
import { useStaticQuery, graphql } from 'gatsby'
import { useDispatch } from 'reactn'
import {
  Box,
  Text,
  Button,
  Divider,
  Flex
} from '@chakra-ui/react'
import { GatsbyImage } from 'gatsby-plugin-image'
import { formatPrice } from '../../../utils/format-price'

const CartUpsells = ({ globalState }) => {
  const { store, updateGlobal } = globalState
  const { cartProducts, checkoutOpen, calculating, showPayment } = store
  const addToCart = useDispatch('addToCart')

  // Use cartProducts from store prop (ReactN updates this via setStore)
  const currentCartProducts = cartProducts || []

  // Create a stable dependency string that changes only when SKUs change
  const cartProductsKey = JSON.stringify(currentCartProducts.map(cp => cp.sku).filter(Boolean).sort())

  // Local state to track upsells added to cart immediately (before global state updates)
  // Use a function initializer to ensure Set is created properly
  const [addedUpsellIds, setAddedUpsellIds] = useState(() => new Set())

  // Use ref to track previous cartProductsKey for comparison (not the array itself)
  const prevCartProductsKeyRef = useRef(null)

  // Query all products with upsellProducts field
  const data = useStaticQuery(graphql`
    query CartUpsellsQuery {
      allShopifyProduct {
        nodes {
          id
          shopifyId
          handle
          title
          upsellProducts {
            id
            shopifyId
            handle
            title
            fields {
              sku
            }
            variants {
              storefrontId
              title
              price
            }
            media {
              ... on ShopifyMediaImage {
                image {
                  altText
                  gatsbyImageData(width: 200)
                }
              }
            }
          }
        }
      }
    }
  `)

  // Build a map of shopifyId/handle to product with upsellProducts
  const productMap = useMemo(() => {
    const map = new Map()
    data.allShopifyProduct.nodes.forEach(product => {
      // Map by shopifyId (gid://shopify/Product/...)
      if (product.shopifyId) {
        map.set(product.shopifyId, product)
      }
      // Also map by handle as fallback
      if (product.handle) {
        map.set(`handle:${product.handle}`, product)
      }
    })
    return map
  }, [data])

  // Get all unique upsell products from cart items
  const upsellProducts = useMemo(() => {
    if (!currentCartProducts || currentCartProducts.length === 0) return []

    const upsells = []
    const seenUpsellIds = new Set()

    currentCartProducts.forEach(cartItem => {
      // Find product by shopifyId (productId in cart item)
      const product = productMap.get(cartItem.productId)

      if (product && product.upsellProducts && product.upsellProducts.length > 0) {
        product.upsellProducts.forEach(upsell => {
          // Skip if already added (avoid duplicates)
          if (seenUpsellIds.has(upsell.shopifyId)) return

          seenUpsellIds.add(upsell.shopifyId)
          upsells.push(upsell)
        })
      }
    })

    return upsells
  }, [currentCartProducts, productMap])

  useEffect(() => {
    // Initialize ref on first run
    if (prevCartProductsKeyRef.current === null) {
      prevCartProductsKeyRef.current = cartProductsKey
      return
    }

    // Get previous key BEFORE updating ref
    const previousKey = prevCartProductsKeyRef.current

    // Check if cartProducts actually changed by comparing keys (strings)
    const hasChanged = previousKey !== cartProductsKey

    // Update ref AFTER comparison for next render
    prevCartProductsKeyRef.current = cartProductsKey

    if (!hasChanged) {
      return
    }

    // Sync local addedUpsellIds with actual cart state
    // If an upsell is no longer in cart, remove it from local state
    const cartProductIds = new Set(currentCartProducts.map(cp => cp.productId).filter(Boolean))
    const cartSkus = new Set(currentCartProducts.map(cp => cp.sku).filter(Boolean))

    setAddedUpsellIds(prev => {
      const newSet = new Set()

      if (prev.size === 0) {
        // If empty, check if any upsells are already in cart (should be in local state)
        // This handles the case where state was reset but items are still in cart
        upsellProducts.forEach(upsell => {
          if (!upsell.shopifyId) return

          const isInCartById = cartProductIds.has(upsell.shopifyId)
          const upsellVariantSku = upsell.variants?.[0]?.storefrontId
          const upsellSku = upsell.fields?.sku
          const isInCartBySku = (upsellVariantSku && cartSkus.has(upsellVariantSku)) ||
                                (upsellSku && cartSkus.has(upsellSku))

          if (isInCartById || isInCartBySku) {
            newSet.add(upsell.shopifyId)
          }
        })

        return newSet.size > 0 ? newSet : prev
      }

      prev.forEach(upsellId => {
        // Check if this upsell is still in cart (by productId or SKU)
        let upsellProduct = null
        for (const product of productMap.values()) {
          if (product.upsellProducts) {
            upsellProduct = product.upsellProducts.find(u => u.shopifyId === upsellId)
            if (upsellProduct) break
          }
        }

        if (upsellProduct) {
          const isInCartById = cartProductIds.has(upsellProduct.shopifyId)
          const upsellVariantSku = upsellProduct.variants?.[0]?.storefrontId
          const upsellSku = upsellProduct.fields?.sku
          const isInCartBySku = (upsellVariantSku && cartSkus.has(upsellVariantSku)) ||
                                (upsellSku && cartSkus.has(upsellSku))

          if (isInCartById || isInCartBySku) {
            // Still in cart - keep it
            newSet.add(upsellId)
          } else {
            // No longer in cart - remove it
          }
        }
      })

      return newSet
    })
  }, [cartProductsKey, currentCartProducts.length, productMap, upsellProducts])


  // Get fetchCart dispatch to recalculate cart after adding/removing upsells
  const fetchCart = useDispatch('fetchCart')

  // Handle adding upsell to cart
  const handleAddUpsell = (upsellProduct) => {
    // Get first variant and first media item
    const firstVariant = upsellProduct.variants?.[0]
    const firstMedia = upsellProduct.media?.[0]

    if (!firstVariant) {
      console.warn('[CartUpsells] No variant found for upsell product:', upsellProduct.title)
      return
    }

    // Immediately mark as added in local state (before global state updates)
    setAddedUpsellIds(prev => {
      const newSet = new Set(prev)
      newSet.add(upsellProduct.shopifyId)
      return newSet
    })

    // Build cart item in same format as addToCart
    const cartItem = {
      title: upsellProduct.title,
      size: firstVariant.title !== "Default Title" ? firstVariant.title : null,
      variants: upsellProduct.variants,
      price: parseFloat(firstVariant.price),
      quantity: 1,
      sku: firstVariant.storefrontId,
      productId: upsellProduct.shopifyId,
      image: firstMedia?.image?.gatsbyImageData || null,
      // Add optional fields that might be needed
      own_garment: false,
      kvatt: false
    }

    addToCart(cartItem)

    // Recalculate cart after adding upsell
    setTimeout(() => {
      fetchCart()
    }, 100) // Small delay to ensure state is updated
  }

  // Filter out upsells that are already in cart
  const availableUpsells = useMemo(() => {
    if (!upsellProducts || upsellProducts.length === 0) return []
    if (!currentCartProducts || currentCartProducts.length === 0) return upsellProducts

    const cartProductIds = new Set(currentCartProducts.map(cp => cp.productId).filter(Boolean))
    const cartSkus = new Set(currentCartProducts.map(cp => cp.sku).filter(Boolean))

    const filtered = upsellProducts.filter(upsell => {
      if (!upsell.shopifyId) return false

      // First check local state (immediately added items)
      if (addedUpsellIds.has(upsell.shopifyId)) {
        return false
      }

      // Check by productId (shopifyId match)
      const isInCartById = cartProductIds.has(upsell.shopifyId)

      // Also check by SKU - check both fields.sku and variant storefrontId
      const upsellSku = upsell.fields?.sku
      const upsellVariantSku = upsell.variants?.[0]?.storefrontId
      const isInCartBySku = (upsellSku && cartSkus.has(upsellSku)) ||
                            (upsellVariantSku && cartSkus.has(upsellVariantSku))

      const isInCart = isInCartById || isInCartBySku

      if (isInCart) {
        // Also add to local state for consistency
        setAddedUpsellIds(prev => {
          const newSet = new Set(prev)
          newSet.add(upsell.shopifyId)
          return newSet
        })
      }

      return !isInCart
    })

    return filtered
  }, [upsellProducts, currentCartProducts, addedUpsellIds])

  // Don't show if checkout is open or no available upsells
  if (checkoutOpen || !availableUpsells || availableUpsells.length === 0) {
    return null
  }

  return (
    <Box id="cartUpsells" mt={4} mb={0}>
      <Text fontSize="12px" fontWeight="300" mb={0} textTransform="uppercase" noOfLines={1}>Would you like to add these items to your cart?</Text>
      <Divider mt={0} mb={2} />

      {availableUpsells.map((upsell) => {
        const firstVariant = upsell.variants?.[0]
        const firstMedia = upsell.media?.find(m => m.image)
        const price = firstVariant ? parseFloat(firstVariant.price) : 0

        return (
          <Box key={upsell.shopifyId || upsell.id} mb={0}>
            <Flex id="upsellflex">
              <Box flex="1" minHeight="50px" pl="0px" ml="0px !important">
                <Flex align="center" height="100%" justify="flex-start">
                  <Box maxWidth={checkoutOpen ? ["80px", "80px", "60px", "100px", "180px"] : ["80px", "80px", "80px", "80px"]} flex="0 0 auto" mr={3} ml="0px">
                    {firstMedia?.image ? (
                      <GatsbyImage
                        image={firstMedia.image.gatsbyImageData}
                        alt={firstMedia.image.altText || upsell.title}
                        style={{ maxHeight: '50px', width: 'auto' }}
                        imgStyle={{ objectFit: 'contain' }}
                      />
                    ) : (
                      <Box width="50px" height="50px" bg="gray.100" />
                    )}
                  </Box>
                  <Box flex="1" minW={0}>
                    <Text fontSize="12px" fontWeight="300" mb={0.5} textTransform="uppercase" noOfLines={1}>
                      {upsell.title}
                    </Text>
                    <Text fontSize="12px" fontWeight="400">
                      {formatPrice(price)}
                    </Text>
                  </Box>
                </Flex>
              </Box>
              <Box margin="auto" flexShrink={0} width="90px">
                <Button
                  variant="voucher"
                  type="button"
                  disabled={calculating || showPayment || !firstVariant}
                  onClick={() => handleAddUpsell(upsell)}
                >
                  ADD
                </Button>
              </Box>
            </Flex>
          </Box>
        )
      })}
    </Box>
  )
}

export default CartUpsells

